import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { siteStatusService } from '../../services/siteStatus.service';
import { SiteOverview } from '../../types/siteStatus.types';
import { USER_ROLES } from '../../constants';
import styles from './SitesOverview.module.css';

export const SitesOverview: React.FC = () => {
  const { user } = useAuth();
  const [sites, setSites] = useState<SiteOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [changingStatus, setChangingStatus] = useState<number | null>(null);
  const [noteModal, setNoteModal] = useState<{ siteId: number; isOpen: boolean } | null>(null);
  const [note, setNote] = useState('');

  const loadSites = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await siteStatusService.getAllSitesOverview();
      setSites(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      console.error('Error loading sites:', err);
      const message = err instanceof Error ? err.message : 'Errore nel caricamento delle sedi';
      setError(message);
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSites();
    // Ricarica ogni 30 secondi per aggiornare i dipendenti attivi
    const interval = setInterval(loadSites, 30000);
    return () => clearInterval(interval);
  }, [loadSites]);

  const handleToggleStatus = async (siteId: number, currentStatus: boolean) => {
    // Mostra modal per la nota
    setNoteModal({ siteId, isOpen: !currentStatus });
  };

  const confirmStatusChange = async () => {
    if (!noteModal) return;

    setChangingStatus(noteModal.siteId);
    setError('');

    try {
      await siteStatusService.changeSiteStatus(noteModal.siteId, {
        isOpen: noteModal.isOpen,
        notes: note || undefined,
      });

      // Ricarica i dati
      await loadSites();
      setNoteModal(null);
      setNote('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel cambio stato';
      setError(message);
    } finally {
      setChangingStatus(null);
    }
  };

  const getTotalActiveEmployees = () => {
    return sites.reduce((sum, site) => sum + site.activeEmployeesCount, 0);
  };

  const getTotalEmployees = () => {
    return sites.reduce((sum, site) => sum + site.totalEmployeesCount, 0);
  };

  const getTotalPresentEmployees = () => {
    return sites.reduce((sum, site) => sum + site.presentEmployeesCount, 0);
  };

  const getOpenSites = () => {
    return sites.filter(s => s.isOpen).length;
  };

  // Verifica se l'utente può cambiare lo stato (manager della sede o owner/superadmin)
  const canChangeStatus = (site: SiteOverview) => {
    if (!user) return false;
    if (user.role <= USER_ROLES.OWNER) return true;
    // Manager può cambiare solo le sue sedi (verifico tramite il nome)
    return user.role === USER_ROLES.MANAGER && site.managerName === user.fullName;
  };

  if (loading) {
    return <div className={styles.loading}>Caricamento...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Panoramica Sedi</h1>
        <button className={styles.refreshButton} onClick={loadSites} title="Aggiorna">
          Aggiorna
        </button>
      </div>

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      {/* Statistics */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{sites.length}</div>
            <div className={styles.statLabel}>Totale Sedi</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{getOpenSites()}</div>
            <div className={styles.statLabel}>Sedi Aperte</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{getTotalActiveEmployees()}</div>
            <div className={styles.statLabel}>Dipendenti Attivi</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{getTotalEmployees()}</div>
            <div className={styles.statLabel}>Totale Dipendenti</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{getTotalPresentEmployees()}</div>
            <div className={styles.statLabel}>Presenti Ora</div>
          </div>
        </div>
      </div>

      {/* Sites Grid */}
      <div className={styles.sitesGrid}>
        {sites.map((site) => (
          <div
            key={site.siteId}
            className={`${styles.siteCard} ${site.isOpen ? styles.open : styles.closed}`}
          >
            <div className={styles.siteHeader}>
              <h3>{site.siteName}</h3>
              <div className={`${styles.statusBadge} ${site.isOpen ? styles.badgeOpen : styles.badgeClosed}`}>
                {site.isOpen ? 'Aperta' : 'Chiusa'}
              </div>
            </div>

            <div className={styles.siteInfo}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Indirizzo:</span>
                <span>{site.address}, {site.city}</span>
              </div>

              {site.managerName && (
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Manager:</span>
                  <span>{site.managerName}</span>
                </div>
              )}

              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Attivi:</span>
                <span className={styles.activeCount}>
                  {site.activeEmployeesCount} / {site.totalEmployeesCount}
                </span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Presenti:</span>
                <span className={styles.activeCount}>
                  {site.presentEmployeesCount} / {site.scheduledEmployeesToday}
                </span>
              </div>

              {site.lastStatusChange && (
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Ultimo cambio:</span>
                  <span className={styles.timestamp}>
                    {new Date(site.lastStatusChange).toLocaleString('it-IT')}
                    {site.changedByUserName && ` da ${site.changedByUserName}`}
                  </span>
                </div>
              )}
            </div>

            {/* Active Employees Bar */}
            {site.totalEmployeesCount > 0 && (
              <div className={styles.progressContainer}>
                <div className={styles.progressLabel}>
                  Dipendenti Operativi: {Math.round((site.activeEmployeesCount / site.totalEmployeesCount) * 100)}%
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${(site.activeEmployeesCount / site.totalEmployeesCount) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {canChangeStatus(site) && (
              <button
                className={`${styles.toggleButton} ${site.isOpen ? styles.buttonClose : styles.buttonOpen}`}
                onClick={() => handleToggleStatus(site.siteId, site.isOpen)}
                disabled={changingStatus === site.siteId}
              >
                {changingStatus === site.siteId
                  ? 'Elaborazione...'
                  : site.isOpen
                  ? 'Chiudi Sede'
                  : 'Apri Sede'
                }
              </button>
            )}
          </div>
        ))}
      </div>

      {sites.length === 0 && !loading && (
        <div className={styles.empty}>
          <p>Nessuna sede trovata</p>
        </div>
      )}

      {/* Note Modal */}
      {noteModal && (
        <div className={styles.modalOverlay} onClick={() => setNoteModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>
                {noteModal.isOpen ? 'Apertura Sede' : 'Chiusura Sede'}
              </h2>
              <button className={styles.closeButton} onClick={() => setNoteModal(null)}>
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <p>
                Stai per segnare la sede come <strong>{noteModal.isOpen ? 'APERTA' : 'CHIUSA'}</strong>.
              </p>
              <p>Puoi aggiungere una nota opzionale:</p>

              <textarea
                className={styles.noteTextarea}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Es: Chiusura anticipata per maltempo"
                rows={3}
              />

              <div className={styles.modalActions}>
                <button className={styles.cancelButton} onClick={() => setNoteModal(null)}>
                  Annulla
                </button>
                <button
                  className={`${styles.confirmButton} ${noteModal.isOpen ? styles.confirmOpen : styles.confirmClose}`}
                  onClick={confirmStatusChange}
                  disabled={changingStatus !== null}
                >
                  {changingStatus !== null ? 'Elaborazione...' : 'Conferma'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

SitesOverview.displayName = 'SitesOverview';
